import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import type { ProjectResource, ChannelResource, ResourceCollection, DeploymentProcessRepository, DeploymentActionResource, ValidateGitRefV2Response, ParentEnvironmentV2Resource, LifecycleResource, Repository, DeploymentProcessResource, } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings, OctopusError, Permission, getParentEnvironments } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { flatMap } from "lodash";
import { useEffect, useMemo } from "react";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { PageContent } from "~/components/PageContent/PageContent";
import List from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { TagIndex } from "~/components/tagSets";
import * as tagSets from "~/components/tagSets";
import { RecentProjects } from "~/utils/RecentProjects/RecentProjects";
import Channel from "./Channel";
export async function channelsPageLoader(repository: Repository, projectContext: Promise<ProjectContextProps>, ephemeralEnvironmentsAreEnabled: boolean): Promise<ChannelsPageLoaderData> {
    const lifecycles = await repository.Lifecycles.all();
    const tagIndex = tagSets.getTagIndex(["Tenant"]);
    const { state: { model: project, projectContextRepository, gitRefValidationError }, } = await projectContext;
    const parentEnvironments = getParentEnvironments(ephemeralEnvironmentsAreEnabled, repository, project);
    const channelsFirstPage = repository.Projects.getChannels(project, 0, 30);
    const deploymentActions = getDeploymentActionsWithErrorHandlingForGitProjects(project, projectContextRepository.DeploymentProcesses, gitRefValidationError, repository);
    return {
        channelsFirstPage: await channelsFirstPage,
        deploymentActions: await deploymentActions,
        lifecycles: lifecycles,
        parentEnvironments: await parentEnvironments,
        tagIndex: await tagIndex,
    };
}
interface ChannelsPageLoaderData {
    channelsFirstPage: ResourceCollection<ChannelResource>;
    lifecycles: LifecycleResource[];
    parentEnvironments: ParentEnvironmentV2Resource[];
    tagIndex: TagIndex;
    deploymentActions: DeploymentActionResource[];
}
interface ChannelsPageProps {
    loaderData: ChannelsPageLoaderData;
    project: ProjectResource;
}
export function ChannelsPage({ loaderData, project }: ChannelsPageProps) {
    useEffect(() => RecentProjects.getInstance().UpdateAccessedProjectIntoLocalStorage(project.Id), [project.Id]);
    const lifecycles = useMemo(() => {
        const lifecycles: Lifecycles = {};
        loaderData.lifecycles.forEach((lifecycle) => {
            lifecycles[lifecycle.Id] = lifecycle;
        });
        return lifecycles;
    }, [loaderData.lifecycles]);
    const parentEnvironments = useMemo(() => {
        const parentEnvironments: ParentEnvironments = {};
        loaderData.parentEnvironments.forEach((parent) => {
            parentEnvironments[parent.Id] = parent;
        });
        return parentEnvironments;
    }, [loaderData.parentEnvironments]);
    const primaryPageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Create Channel",
        path: links.createChannelPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }),
        hasPermissions: isAllowed({ permission: Permission.ProcessEdit, project: project.Id, tenant: "*" }),
    };
    return (<PageContent header={{ title: channelsPageTitle, primaryAction: primaryPageAction }}>
            <ChannelsList initialData={loaderData.channelsFirstPage} onRow={(item) => buildRow(item, project, lifecycles, parentEnvironments, loaderData.tagIndex, loaderData.deploymentActions)} onRowRedirectUrl={(channel: ChannelResource) => links.channelPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, channelId: channel.Id })} onRowAccessibleName={(channel: ChannelResource) => `${channel.Name}`} onFilter={filter} filterSearchEnabled={true} apiSearchParams={["partialName"]} filterHintText="Filter by name..."/>
        </PageContent>);
}
async function getDeploymentActionsWithErrorHandlingForGitProjects(project: ProjectResource, deploymentProcessRepository: DeploymentProcessRepository, gitRefValidationError: ValidateGitRefV2Response | undefined, repository: Repository) {
    try {
        if (gitRefValidationError) {
            return [];
        }
        else {
            let process: DeploymentProcessResource | undefined = undefined;
            if (project.ProjectTemplateDetails) {
                process = await repository.TemplatedProjects.getTemplatedProjectDeploymentProcess(project.Id);
            }
            else {
                process = await deploymentProcessRepository.get();
            }
            return flatMap(process.Steps, (step) => step.Actions);
        }
    }
    catch (exception) {
        if (HasGitPersistenceSettings(project.PersistenceSettings) && OctopusError.isOctopusError(exception)) {
            // We only want to catch the error if it's a Git project and an Octopus exception. Otherwise, error
            // as usual
            return [];
        }
        else {
            throw exception;
        }
    }
}
function filter(filter: string, resource: ChannelResource) {
    return !filter || filter.length === 0 || !resource || resource.Name.toLowerCase().includes(filter.toLowerCase());
}
function buildRow(channel: ChannelResource, project: ProjectResource, lifecycles: Lifecycles, parentEnvironments: ParentEnvironments, tagIndex: TagIndex, deploymentActions: DeploymentActionResource[]) {
    return <Channel key={channel.Id} channel={channel} project={project} lifecycles={lifecycles} tagIndex={tagIndex} parentEnvironments={parentEnvironments} deploymentActions={deploymentActions}/>;
}
export interface Lifecycles {
    [name: string]: LifecycleResource;
}
export interface ParentEnvironments {
    [name: string]: ParentEnvironmentV2Resource;
}
class ChannelsList extends List<ChannelResource> {
}
export const channelsPageTitle = "Channels";
